import React from 'react';
import { PosBarcodeImportModal } from '../components/modals/PosBarcodeImportModal';
import { PosVariousModalContainer } from '../components/modals/PosVariousModalContainer';
import { PosRecargaModalContainer } from '../components/modals/PosRecargaModalContainer';
import { PosProductPressModals } from '../components/modals/PosProductPressModals';
import { PosCheckoutModalContainer } from '../components/modals/PosCheckoutModalContainer';
import { PosMpQrModalContainer } from '../components/modals/PosMpQrModalContainer';
import { DiningTablePickerModal } from '../components/modals/DiningTablePickerModal';
import { BarcodeScannerModal } from '../../shared/ui/BarcodeScannerModal';
import { PosManualBarcodeModal } from '../components/modals/PosManualBarcodeModal';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { CatalogCategorySelectorModal } from '../../catalog/public-api';
import type { Product } from '../../catalog/domain-api'
import type { ProductVariant } from '../../catalog/domain-api'
import type { RemoteBarcodeHit } from '../../catalog/domain-api'
import type { CategoryOption } from '../../catalog/domain-api';
import type { MasterFilterOption } from '../../shared/filters/MasterFilterSheetModal';
import type { CartItem } from '../store/usePosStore';
import type { CartEvaluationResult } from '../../promotions/public-api';
import type { CartPromoUiStatus } from '../application/posCartPromoPresentation';
import type { PosCheckoutCustomerOption, PosPaymentMethodId, PosStockSalePolicy } from '../domain/posTypes';
import type { PosBarcodeImportConfirmPayload } from '../components/modals/PosBarcodeImportModal';
import type { DiningTable } from '../../hospitality/domain/hospitalityTypes';
import type { ModalPickerOption } from '../../shared/modals/ModalNestedPicker';

type StockTapNotice = { product: Product; policy: PosStockSalePolicy };
type StockTapNoticeCopy = { variant: PosStockSalePolicy; title: string; body: string; canAdd: boolean };

type ScanMissSheetProps = {
  visible: true;
  title: string;
  options: MasterFilterOption[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onClose: () => void;
  showAllOption: false;
};

export type PosScreenModalsProps = {
  barcodeImport: {
    remoteImportVisible: boolean;
    remoteImportHit: RemoteBarcodeHit | null;
    remoteImportSaving: boolean;
    importError: string | null;
    closeRemoteImport: () => void;
    handleConfirmBarcodeImport: (payload: PosBarcodeImportConfirmPayload) => void;
    importCategoryOptions: ModalPickerOption[];
    defaultImportCategoryId: string | null;
  };
  scanner: {
    scannerVisible: boolean;
    manualBarcodeVisible: boolean;
    barcodeAllowCamera: boolean;
    setScannerVisible: (v: boolean) => void;
    setManualBarcodeVisible: (v: boolean) => void;
    handleScannerResult: (code: string) => void;
  };
  dining: {
    diningTablePickerVisible: boolean;
    diningTablePickerLoading: boolean;
    diningTableOptions: DiningTable[];
    confirmDiningTableAndCheckout: (tableId: string | null) => void;
    setDiningTablePickerVisible: (v: boolean) => void;
  };
  categorySelector: {
    categorySelectorVisible: boolean;
    categoryOptions: CategoryOption[];
    selectedCategory: string | 'all' | null;
    itemTypeFilter: string;
    setCategorySelectorVisible: (v: boolean) => void;
    handleSelectCategoryFromMore: (category: string | 'all') => void;
  };
  productPress: {
    stockTapNotice: StockTapNotice | null;
    setStockTapNotice: (n: StockTapNotice | null) => void;
    stockTapNoticeCopy: StockTapNoticeCopy | null;
    handleAddOutOfStockProduct: () => void;
    openInventoryForProduct: (p: Product) => void;
    weightProduct: Product | null;
    setWeightProduct: (p: Product | null) => void;
    hourProduct: Product | null;
    setHourProduct: (p: Product | null) => void;
    variantPickerProduct: Product | null;
    setVariantPickerProduct: (p: Product | null) => void;
    variantPickerVariants: ProductVariant[];
    setVariantPickerVariants: (v: ProductVariant[]) => void;
    pendingVariantId: string | null;
    setPendingVariantId: (id: string | null) => void;
    handleVariantSelect: (variant: ProductVariant) => void;
    menuOptionsProduct: Product | null;
    setMenuOptionsProduct: (p: Product | null) => void;
    menuOptionsGroups: import('../../catalog/public-api').MenuOptionGroup[];
    menuOptionsLoading?: boolean;
    /** Gap 4: pre-selección al editar una línea existente. */
    initialMenuOptionsSelected?: import('../../catalog/public-api').SelectedMenuOption[];
    initialMenuOptionsLineNote?: string | null;
    handleMenuOptionsConfirm: (
      selected: import('../../catalog/public-api').SelectedMenuOption[],
      lineNote: string,
    ) => void;
    currentBranchId?: string | null;
    stockSalePolicy?: import('../domain/posTypes').PosStockSalePolicy;
    addProductToCart: (
      p: Product,
      q?: number | import('../store/usePosStore').AddProductToCartOptions,
    ) => void;
  };
  checkoutModal: {
    cart: CartItem[];
    cartEvaluation: CartEvaluationResult | null;
    promoUiStatus?: CartPromoUiStatus;
    canOpenCashManagementFromPos: boolean;
    allowFiscalSaleMode: boolean;
    currentBranchId: string | null | undefined;
    cashSessionReady: boolean;
    total: number;
    merchandiseTotal?: number;
    tipAmount?: number;
    tipUiEnabled?: boolean;
    tipIntent?: import('../application/posCheckoutTip').PosTipIntent | null;
    selectTipIntent?: (next: import('../application/posCheckoutTip').PosTipIntent | null) => void;
    totalIngresado: number;
    vuelto: number;
    saldoPendiente: number;
    isSaving: boolean;
    hasBlockingStockIssue: boolean;
    hasExpiredStock: boolean;
    customerEligibleCustomers: PosCheckoutCustomerOption[];
    completeSale: () => void;
    updateDraft: (id: string, patch: Record<string, unknown>) => void;
    removeDraft: (id: string) => void;
    addSplitPayment: (methodId?: PosPaymentMethodId) => void;
    handleRequestStockCorrection: () => void;
    handleOpenCashManagementFromCheckout: () => void;
    loadMoreCustomers: (pageIndex: number, loadAll?: boolean) => void;
    onSetCartLineAttribution?: (lineId: string, performedByUserId: string | null, soldByUserId: string | null) => void;
    overlayModal?: React.ReactNode;
  };
  mpQr: {
    currentBranchId: string | null | undefined;
    userId: string | null | undefined;
    mercadopagoQrMethodAllowed: boolean;
    clearPersistenceAfterSale: () => void | Promise<void>;
    clearPendingSaleId: () => void;
    onMpQrApproved?: () => void;
  };
  scanMissSheet: ScanMissSheetProps | null;
  confirmModals: {
    cartConfirm: React.ReactNode;
    checkoutConfirm: React.ReactNode;
  };
};

export const PosScreenModals = ({
  barcodeImport,
  scanner,
  dining,
  categorySelector,
  productPress,
  checkoutModal,
  mpQr,
  scanMissSheet,
  confirmModals,
}: PosScreenModalsProps) => {
  return (
    <>
      <PosBarcodeImportModal
        visible={barcodeImport.remoteImportVisible}
        remoteHit={barcodeImport.remoteImportHit}
        categoryOptions={barcodeImport.importCategoryOptions}
        defaultCategoryId={barcodeImport.defaultImportCategoryId}
        saving={barcodeImport.remoteImportSaving}
        importError={barcodeImport.importError}
        onClose={barcodeImport.closeRemoteImport}
        onConfirm={(payload) => {
          void barcodeImport.handleConfirmBarcodeImport(payload);
        }}
      />

      <PosVariousModalContainer />
      <PosRecargaModalContainer />

      <DiningTablePickerModal
        visible={dining.diningTablePickerVisible}
        loading={dining.diningTablePickerLoading}
        options={dining.diningTableOptions}
        onSelect={dining.confirmDiningTableAndCheckout}
        onClose={() => dining.setDiningTablePickerVisible(false)}
      />

      <PosCheckoutModalContainer {...checkoutModal} />

      <PosMpQrModalContainer {...mpQr} />

      <PosProductPressModals {...productPress} />

      <CatalogCategorySelectorModal
        visible={categorySelector.categorySelectorVisible}
        title="Categorías"
        subtitle="Elegí una categoría para filtrar."
        options={categorySelector.categoryOptions}
        value={categorySelector.selectedCategory ?? ''}
        emptyLabel="No hay categorías con eso."
        searchPlaceholder="Buscar categoría…"
        showScopePills={categorySelector.itemTypeFilter === 'all'}
        onClose={() => categorySelector.setCategorySelectorVisible(false)}
        onSelect={categorySelector.handleSelectCategoryFromMore}
      />

      {scanner.barcodeAllowCamera ? (
        <BarcodeScannerModal
          visible={scanner.scannerVisible}
          title="Escanear para vender"
          subtitle="Leé el código y entra al carrito."
          onClose={() => scanner.setScannerVisible(false)}
          onCodeScanned={scanner.handleScannerResult}
        />
      ) : (
        <PosManualBarcodeModal
          visible={scanner.manualBarcodeVisible}
          onClose={() => scanner.setManualBarcodeVisible(false)}
          onCodeScanned={(code) => {
            scanner.setManualBarcodeVisible(false);
            scanner.handleScannerResult(code);
          }}
        />
      )}

      {confirmModals.cartConfirm}
      {confirmModals.checkoutConfirm}
      {scanMissSheet ? <MasterFilterSheetModal {...scanMissSheet} /> : null}
    </>
  );
};
